import React, { useEffect, useState } from "react";
import { TrendingUpIcon, RefreshCwIcon } from "lucide-react";
import { Button, Table, Progress, Alert } from "antd";
import {
  LoadingOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import ReactECharts from "echarts-for-react";
import { useGeoLiftForm } from "../../../hooks/useGeoLiftForm";
import useGeoLiftDataProcessor, {
  formatMarketName,
  isGeoLiftResponse,
  MarketResult,
} from "../../../pages/measurement/geo-lift/components/GeoLiftDataProcessor";
import MarketDetailsModal from "../../../pages/measurement/geo-lift/components/MarketDetailsModal";
import CompactAILoader from "./CompactAILoader";

interface ViewResultsMeasurePerformanceProps {
  jobConfigId?: string;
  mode: any;
}

const ViewResultsMeasurePerformance: React.FC<
  ViewResultsMeasurePerformanceProps
> = ({ jobConfigId, mode }) => {
  const {
    form,
    loading,
    testMarkets,
    csvData,
    syntheticMarketOptions,
    csvOutput,
    handleTestMarketChange,
  } = useGeoLiftForm(jobConfigId ?? "");

  const [currentSyntheticMarket, setCurrentSyntheticMarket] =
    useState<string>("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<string>("");
  const [currentMarketData, setCurrentMarketData] =
    useState<MarketResult | null>(null);

  // Update useEffect to set initial synthetic market when options change
  useEffect(() => {
    if (syntheticMarketOptions && syntheticMarketOptions.length > 0) {
      setCurrentSyntheticMarket(syntheticMarketOptions[0].label);
    }
  }, [syntheticMarketOptions]);

  const {
    marketResults,
    batchProcessing,
    currentProcessingIndex,
    aggregateData,
    processAllMarkets,
  } = useGeoLiftDataProcessor({
    jobConfigId,
    form,
    testMarkets,
    currentSyntheticMarket,
    handleTestMarketChange,
    mode,
  });

  // Function to render the aggregate data summary card
  const renderAggregateDataSummary = (): React.ReactNode => {
    if (!aggregateData) return null;

    // Prepare bar chart data
    const controlValue = Math.round(
      aggregateData?.lift?.totalSalesWithoutCampaign ?? 0
    );
    const testValue = Math.round(
      aggregateData?.lift?.totalSalesWithCampaign ?? 0
    );

    // Format value for display
    const formatValue = (value: number): string => {
      if (value >= 1_000_000) {
        return (value / 1_000_000).toFixed(1) + "M";
      } else if (value >= 1_000) {
        return (value / 1_000).toFixed(1) + "K";
      }
      return value.toLocaleString();
    };

    // Horizontal bar chart configuration
    const chartOption = {
      grid: {
        left: "15%",
        right: "10%",
        top: "10%",
        bottom: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        axisLabel: {
          formatter: (value: number) => formatValue(value),
          fontSize: 12,
          color: "#4B5563",
        },
        splitLine: {
          lineStyle: {
            color: "#E5E7EB",
          },
        },
      },
      yAxis: {
        type: "category",
        data: ["Control", "Test"],
        axisLabel: {
          fontSize: 14,
          color: "#1F2937",
          fontWeight: 500,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
      },
      series: [
        {
          type: "bar",
          data: [
            {
              value: controlValue,
              itemStyle: { color: "#6B7280" }, // gray for control
            },
            {
              value: testValue,
              itemStyle: { color: "#0055B8" }, // brand blue for test
            },
          ],
          barWidth: "50%",
          label: {
            show: true,
            position: "right",
            formatter: (params: any) => `$ ${formatValue(params.value)}`,
            fontSize: 12,
            fontWeight: "bold",
            color: "#1F2937",
          },
        },
      ],
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any) => {
          const param = params[0];
          return `${param.name}: $ ${param.value.toLocaleString()}`;
        },
      },
    };

    return (
      <div className="mb-6 bg-white p-3 rounded-xl shadow-sm border-2 border-blue-100">
        <div className="flex justify-center items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800 text-center">
            {testMarkets?.length === 1
              ? "Campaign Results"
              : "Aggregate Campaign Results"}
          </h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left side - Metric cards stacked vertically */}
          <div className="lg:col-span-1 flex flex-col gap-4">
            <div className="bg-[#0055B8] p-4 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <div className="flex items-center mb-2">
                  <TrendingUpIcon className="h-5 w-5 mr-2 text-white/80" />
                  <span className="font-medium text-white/90">Total Lift</span>
                </div>
                <div
                  className={`text-2xl font-bold ${
                    (aggregateData?.lift?.totalLiftPercentage ?? 0) >= 0
                      ? "text-green-400"
                      : "text-red-400"
                  }`}
                >
                  {(aggregateData?.lift?.totalLiftPercentage ?? 0) >= 0
                    ? "+"
                    : ""}
                  {aggregateData?.lift?.totalLiftPercentage.toFixed(2)}%
                </div>
                <div
                  className={`text-base font-bold ${
                    (aggregateData?.lift?.totalLiftPercentageLowerBound ?? 0) >= 0
                      ? "text-green-400"
                      : "text-red-400"
                  }`}
                >
                  {(aggregateData?.lift?.totalLiftPercentageLowerBound ?? 0) >= 0
                    ? "+"
                    : ""}
                  {aggregateData?.lift?.totalLiftPercentageLowerBound.toFixed(2)}%

                  <span>"-"</span>
                {(aggregateData?.lift?.totalLiftPercentageUpperBound ?? 0) >= 0
                    ? "+"
                    : ""}
                  {aggregateData?.lift?.totalLiftPercentageUpperBound.toFixed(2)}%

                </div>

              </div>
            </div>

            <div className="bg-[#0055B8] p-4 rounded-lg">
              <div className="flex flex-col items-center text-center">
                <span className="font-medium text-blue-100 mb-2">
                  Incremental Outcome
                </span>
                <div className="text-2xl font-bold text-white">
                  ${" "}
                  {Math.round(
                    aggregateData?.roas?.totalIncrementSales ?? 0
                  ).toLocaleString("en-US")}
                </div>
              </div>
            </div>

            {aggregateData?.roas && (
              <div className="bg-[#0055B8] p-4 rounded-lg">
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center mb-2">
                    {/* <DollarSignIcon className="h-5 w-5 text-white/80 mr-2" /> */}
                    <span className="font-medium text-blue-100">iROAS</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {aggregateData?.roas?.totalROAS.toFixed(2)}x
                  </div>
                  <div className="text-base font-bold text-white">
                    {aggregateData?.roas?.totalROASLowerBound.toFixed(2)}x - {aggregateData?.roas?.totalROASUpperBound.toFixed(2)}x
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right side - Horizontal bar chart */}
          <div className="lg:col-span-2 bg-gray-50 p-4 rounded-lg flex flex-col">
            <h4 className="text-md font-semibold text-gray-800 mb-3 text-center">
              Control vs. Test Outcome
            </h4>
            <div className="flex-1">
              <ReactECharts
                option={chartOption}
                style={{ height: "100%", minHeight: "200px" }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-0">
      {loading ? (
        <div className="flex justify-center items-center">
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-3xl shadow-2xl p-12 w-full max-w-md text-center border border-blue-400/30 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-blue-500/10 animate-pulse"></div>
            <div className="relative z-10">
              <div className="flex justify-center mb-6">
                <CompactAILoader />
              </div>
              <p className="text-white font-bold text-xl mb-3 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">
                Generating Insights
              </p>
              <p className="text-blue-300 text-sm leading-relaxed">
                Our AI is analyzing patterns and preparing actionable
                recommendations
              </p>
            </div>
          </div>{" "}
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            {marketResults.length > 0 && (
              <div className="mt-0 bg-white p-1 rounded-xl shadow-sm">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    All Markets Results
                  </h3>
                  <div className="flex gap-2 mb-4">
                    <Button
                      type="primary"
                      onClick={() => processAllMarkets()}
                      disabled={batchProcessing}
                      className="py-4 text-lg"
                      icon={
                        batchProcessing ? (
                          <SyncOutlined spin />
                        ) : (
                          <RefreshCwIcon className="h-4 w-4 mr-2" />
                        )
                      }
                      loading={batchProcessing}
                      size="large"
                    >
                      {batchProcessing
                        ? "Processing Markets..."
                        : "Process All Markets"}
                    </Button>
                  </div>
                </div>
                {/* Render aggregate data summary if available */}
                {aggregateData && renderAggregateDataSummary()}

                <div>
                  {batchProcessing && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg shadow-sm">
                      <div className="flex items-center mb-3">
                        <LoadingOutlined
                          style={{
                            fontSize: 24,
                            color: "#1890ff",
                            marginRight: 12,
                          }}
                          spin
                        />
                        <div className="text-blue-700 font-medium">
                          Processing Markets: {currentProcessingIndex + 1} of{" "}
                          {marketResults.length}
                        </div>
                      </div>
                      <div className="mb-3">
                        <Progress
                          percent={Math.round(
                            ((currentProcessingIndex + 1) /
                              marketResults.length) *
                              100
                          )}
                          status="active"
                          strokeColor={{
                            "0%": "#108ee9",
                            "100%": "#87d068",
                          }}
                        />
                      </div>
                      <div className="text-gray-500 text-sm">
                        Currently processing:{" "}
                        <span className="font-medium text-blue-600">
                          {marketResults[currentProcessingIndex]?.marketName
                            ? formatMarketName(
                                marketResults[currentProcessingIndex].marketName
                              )
                            : "Preparing..."}
                        </span>
                      </div>
                    </div>
                  )}

                  {!batchProcessing &&
                    marketResults.some(
                      (result) => result.status === "error"
                    ) && (
                      <Alert
                        message="Processing Errors"
                        description={
                          <div>
                            <div className="font-medium mb-1">
                              The following markets encountered errors:
                            </div>
                            <ul className="list-disc pl-5">
                              {marketResults
                                .filter((r) => r.status === "error")
                                .map((market, index) => (
                                  <li key={index}>
                                    {formatMarketName(market.marketName)}:{" "}
                                    {market.error || "Unknown error"}
                                  </li>
                                ))}
                            </ul>
                          </div>
                        }
                        type="error"
                        showIcon
                        icon={<WarningOutlined />}
                        className="mt-4"
                      />
                    )}
                </div>
                <Table
                  dataSource={marketResults}
                  rowKey="marketName"
                  pagination={false}
                  className="mt-4"
                  columns={[
                    {
                      title: "#",
                      dataIndex: "index",
                      key: "marketName",
                      render: (index) => (
                        <span className="font-medium">{index}</span>
                      ),
                    },
                    {
                      title: "Test Markets",
                      dataIndex: "marketName",
                      key: "marketName",
                      render: (text) => (
                        <span className="font-medium">
                          {formatMarketName(text)}
                        </span>
                      ),
                    },
                    {
                      title: "Incremental outcome",
                      key: "incremental",
                      render: (_, record) => {
                        if (record.status !== "completed" || !record.data) {
                          return "-";
                        }

                        try {
                          let sales;
                          // Check if data is in the expected format
                          if (
                            record.data.data &&
                            typeof record.data.data === "object"
                          ) {
                            // API response structure
                            const responseData = record.data.data;

                            if (isGeoLiftResponse(responseData)) {
                              sales = responseData.incremental;
                            } else if (
                              responseData.lift &&
                              typeof responseData.lift === "object"
                            ) {
                              sales = responseData.lift.incremental;
                            } else {
                              return "-";
                            }
                          } else {
                            // Direct data structure (fallback)
                            if (isGeoLiftResponse(record.data)) {
                              sales = record.data.incremental;
                            } else if (
                              record.data.lift &&
                              typeof record.data.lift === "object"
                            ) {
                              sales = record.data.lift.incremental;
                            } else {
                              return "-";
                            }
                          }

                          if (typeof sales !== "number") {
                            return "-";
                          }

                          return `$${Math.round(sales).toLocaleString()}`;
                        } catch (error) {
                          return "-";
                        }
                      },
                    },
                    {
                      title: "Lift",
                      key: "lift",
                      render: (_, record) => {
                        if (record.status !== "completed" || !record.data) {
                          return "-";
                        }

                        let liftPercentage;
                        try {
                          // Check if data is in the expected format
                          if (
                            record.data.data &&
                            typeof record.data.data === "object"
                          ) {
                            // API response structure
                            const responseData = record.data.data;

                            if (isGeoLiftResponse(responseData)) {
                              liftPercentage = responseData.lift_percentage;
                            } else if (
                              responseData.lift &&
                              typeof responseData.lift === "object"
                            ) {
                              liftPercentage =
                                responseData.lift.totalLiftPercentage;
                            } else {
                              return "-";
                            }
                          } else {
                            // Direct data structure (fallback)
                            if (isGeoLiftResponse(record.data)) {
                              liftPercentage = record.data.lift_percentage;
                            } else if (
                              record.data.lift &&
                              typeof record.data.lift === "object"
                            ) {
                              liftPercentage =
                                record.data.lift.totalLiftPercentage;
                            } else {
                              return "-";
                            }
                          }

                          if (typeof liftPercentage !== "number") {
                            return "-";
                          }

                          return (
                            <span
                              className={`font-medium ${
                                liftPercentage >= 0
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {liftPercentage.toFixed(2)}%
                            </span>
                          );
                        } catch (error) {
                          console.error("Error rendering lift %:", error);
                          return "-";
                        }
                      },
                    },

                    {
                      title: "Statistical Significance",
                      key: "salesWithoutCampaign",
                      render: (_, record) => {
                        if (record.status !== "completed" || !record.data) {
                          return "-";
                        }

                        try {
                          let sales;
                          // Check if data is in the expected format
                          if (
                            record.data.data &&
                            typeof record.data.data === "object"
                          ) {
                            // API response structure
                            const responseData = record.data.data;

                            if (isGeoLiftResponse(responseData)) {
                              sales = responseData.pvalue;
                            } else if (
                              responseData.lift &&
                              typeof responseData.lift === "object"
                            ) {
                              sales = responseData.lift.pvalue;
                            } else {
                              return "-";
                            }
                          } else {
                            // Direct data structure (fallback)
                            if (isGeoLiftResponse(record.data)) {
                              sales = record.data.pvalue;
                            } else if (
                              record.data.lift &&
                              typeof record.data.lift === "object"
                            ) {
                              sales = record.data.lift.pvalue;
                            } else {
                              return "-";
                            }
                          }

                          if (typeof sales !== "number") {
                            return "-";
                          }

                          return <span>{((1 - sales) * 100).toFixed(2)}%</span>;
                        } catch (error) {
                          return "-";
                        }
                      },
                    },
                    {
                      title: "",
                      key: "action",
                      render: (_, record) => {
                        if (record.status !== "completed") {
                          return null;
                        }

                        return (
                          <Button
                            type="primary"
                            onClick={() => {
                              // Set the current market data for the modal
                              setModalContent(record.marketName);
                              setCurrentMarketData(record.data);
                              // // Open the modal
                              setIsModalOpen(true);
                            }}
                          >
                            View Details
                          </Button>
                        );
                      },
                    },
                  ]}
                />
              </div>
            )}
          </div>
          {/* Market Details Modal */}
          <MarketDetailsModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            currentMarketData={currentMarketData}
            modalContent={modalContent}
          />
        </div>
      )}
    </div>
  );
};

export default ViewResultsMeasurePerformance;
